import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createFieldHelp,
} from '../utils';

export const projectsHelp: HelpContent = {
  id: 'projects',
  title: 'Projects',
  description: 'Project management, tracking, and collaboration',
  lastUpdated: '2026-02-04',
  sections: [
    {
      id: 'overview',
      title: 'Overview',
      content: '<p>The Projects section helps you manage and track all your project work. View project details, monitor progress, and collaborate with team members effectively.</p>'
    },
    {
      id: 'project-list',
      title: 'Project List',
      content: '<p>The main projects view shows all projects you have access to. Each project card displays:</p><ul><li><strong>Project Name</strong> - The project identifier</li><li><strong>Status</strong> - Active, completed, on hold, etc.</li><li><strong>Client</strong> - Associated client or stakeholder</li><li><strong>Progress</strong> - Overall completion percentage</li><li><strong>Team</strong> - Assigned team members</li></ul>'
    },
    {
      id: 'project-detail',
      title: 'Project Details',
      content: '<p>Click on a project to view its full details including:</p><ul><li><strong>Description</strong> - Project scope and objectives</li><li><strong>Timeline</strong> - Start date, end date, milestones</li><li><strong>Tasks & Stories</strong> - Work items and their status</li><li><strong>Team Members</strong> - Who\'s working on the project</li><li><strong>Documents</strong> - Related files and attachments</li></ul>'
    },
    {
      id: 'tasks',
      title: 'Working with Tasks',
      content: '<p>Tasks are the individual work items within a project. You can:</p><ul><li>View tasks assigned to you</li><li>Update task status (To Do, In Progress, Done)</li><li>Log time spent on tasks</li><li>Add comments and attachments</li><li>Track dependencies between tasks</li></ul>'
    }
  ],
  faqs: [
    {
      id: 'faq-1',
      question: 'How do I get assigned to a project?',
      answer: 'Project managers and team leads assign team members to projects. If you need to be added to a project, contact the project manager or your team lead.',
      tags: ['assign', 'join', 'access', 'team']
    },
    {
      id: 'faq-2',
      question: 'Can I create a new project?',
      answer: 'Project creation is typically limited to project managers and team leads. If you need a new project created, submit a request through your manager or the project management office.',
      tags: ['create', 'new', 'start', 'permissions']
    },
    {
      id: 'faq-3',
      question: 'How do I log time against a project?',
      answer: 'Use the Timesheet page to log hours against specific projects and tasks. You can also log time directly from the task detail view.',
      tags: ['time', 'hours', 'log', 'timesheet']
    },
    {
      id: 'faq-4',
      question: 'What do the project statuses mean?',
      answer: 'Projects can have several statuses: Active (ongoing work), On Hold (temporarily paused), Completed (finished), and Cancelled (stopped). The status helps filter and prioritize your work.',
      tags: ['status', 'active', 'completed', 'hold']
    },
    {
      id: 'faq-5',
      question: 'How do I update my task progress?',
      answer: 'Click on a task to open its details, then use the status dropdown to change it. You can move tasks between To Do, In Progress, Review, and Done stages.',
      tags: ['update', 'task', 'progress', 'status']
    },
    {
      id: 'faq-6',
      question: 'Where can I see all my assigned tasks across projects?',
      answer: 'The "My Tasks" page shows all tasks assigned to you across all projects in one view. Access it from the sidebar navigation.',
      tags: ['my tasks', 'assigned', 'all', 'view']
    }
  ],
  relatedLinks: [
    { title: 'My Tasks', href: '/my-tasks', description: 'View all your assigned tasks' },
    { title: 'Timesheet', href: '/timesheet', description: 'Log your work hours' },
    { title: 'Clients', href: '/clients', description: 'View client information' },
    { title: 'Resources', href: '/resources', description: 'Resource management and allocation' }
  ],
  keywords: ['project management', 'tasks', 'work', 'team', 'deliverables', 'milestones']
};

// ============================================================================
// SECTION-LEVEL HELP: NEW PROJECT DIALOG
// ============================================================================

/**
 * New Project Dialog help
 * Use with: <SectionHelp contentId="projects:new-project">
 */
export const newProjectDialogHelp: HelpContent = createHelpContent({
  id: 'projects:new-project',
  title: 'Create New Project',
  description: 'Set up a new project for your team',
  lastUpdated: '2026-02-04',
  parentId: 'projects',

  tips: [
    createTip(
      'tip-objective',
      'pro-tip',
      'Write Clear Objectives',
      'A well-defined objective helps team members understand the project\'s purpose and goals. Include measurable outcomes when possible.'
    ),
    createTip(
      'tip-teams',
      'pro-tip',
      'Team Visibility',
      'Use "Visible to All Teams" for company-wide initiatives. For confidential or department-specific projects, select only the relevant teams.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Creating a Project',
      `<p>This form allows you to set up a new project in the system. Required fields are marked with a red asterisk (*).</p>
      <p>After creation, you can add team members, create tasks, and start tracking progress.</p>`
    ),
    createSection(
      'required-fields',
      'Required Information',
      `<p>The following fields must be completed:</p>
      <ul>
        <li><strong>Project Name</strong> - A unique, descriptive name for the project</li>
        <li><strong>Objective</strong> - What the project aims to achieve</li>
        <li><strong>Start Date</strong> - When work begins</li>
        <li><strong>Status</strong> - Current state of the project</li>
        <li><strong>Project Manager</strong> - Person responsible for oversight</li>
        <li><strong>Team Visibility</strong> - Who can see and access this project</li>
      </ul>`
    ),
    createSection(
      'team-visibility',
      'Understanding Team Visibility',
      `<p>Team visibility controls which teams can see and work on this project:</p>
      <ul>
        <li><strong>Visible to All Teams</strong> - Everyone in the organization can see the project</li>
        <li><strong>Specific Teams</strong> - Only selected teams can access the project. Use this for:</li>
        <ul>
          <li>Department-specific projects</li>
          <li>Confidential initiatives</li>
          <li>Client-restricted work</li>
        </ul>
      </ul>
      <p><em>Note: Project managers and admins can always see all projects regardless of visibility settings.</em></p>`
    ),
    createSection(
      'status-options',
      'Project Status Options',
      `<p>Choose the appropriate status for your project:</p>
      <ul>
        <li><strong>Not Started</strong> - Project is planned but work hasn't begun</li>
        <li><strong>In Progress</strong> - Active work is being done</li>
        <li><strong>On Hold</strong> - Temporarily paused (waiting for resources, approvals, etc.)</li>
        <li><strong>At Risk</strong> - Project has issues that may affect delivery</li>
        <li><strong>Cancelled</strong> - Project has been stopped</li>
        <li><strong>Completed</strong> - All work is finished</li>
      </ul>`
    ),
    createSection(
      'optional-fields',
      'Optional Fields',
      `<p>These fields provide additional context but aren't required:</p>
      <ul>
        <li><strong>End Date</strong> - Target completion date</li>
        <li><strong>Client</strong> - External client associated with the project</li>
        <li><strong>Departments</strong> - Functional areas involved</li>
        <li><strong>Tags</strong> - Keywords for categorization and search</li>
      </ul>`
    ),
  ],

  faqs: [
    createFAQ(
      'faq-who-create',
      'Who can create new projects?',
      'Users with Admin or Manager roles can create new projects. If you need a project created, contact your manager or a system administrator.',
      ['permissions', 'create', 'roles']
    ),
    createFAQ(
      'faq-change-later',
      'Can I change project details after creation?',
      'Yes, project managers and admins can edit project details at any time from the project settings page.',
      ['edit', 'update', 'change', 'modify']
    ),
    createFAQ(
      'faq-add-members',
      'How do I add team members after creating the project?',
      'After creation, go to the project details page and use the "Team" tab to add or remove team members and set their roles.',
      ['team', 'members', 'add', 'assign']
    ),
    createFAQ(
      'faq-visibility-change',
      'Can I change team visibility after the project is created?',
      'Yes, you can modify team visibility from the project settings. Note that changing visibility may affect who can see project history and data.',
      ['visibility', 'teams', 'change', 'access']
    ),
    createFAQ(
      'faq-tags-purpose',
      'What are project tags used for?',
      'Tags help categorize and filter projects. They appear in search results and can be used to group related projects. Examples: "Q1-2026", "client-x", "internal".',
      ['tags', 'categories', 'filter', 'search']
    ),
  ],

  relatedLinks: [
    { title: 'Project List', href: '/projects', description: 'View all projects' },
    { title: 'Resources', href: '/resources', description: 'Manage team allocations' },
    { title: 'Clients', href: '/clients', description: 'View client information' },
  ],

  keywords: ['new project', 'create project', 'project form', 'team visibility', 'project setup'],
});
